<script lang="ts">
	import { concatGoals, concatPenalties } from '../hockeydata.js';
	import type { HDExtraData } from '../types/util/ExtraData.js';
	import type {
		HDGameReportResponseData,
		HDGoal,
		HDPenalty
	} from '../types/responses/GameReport.js';
	import { GameStatus } from '../types/util/GameStatus.js';
	import CollapsibleCard from '#lib/components/CollapsibleCard.svelte';

	let {
		game
	}: {
		game: HDGameReportResponseData;
	} = $props();

	let goals = $derived.by(() =>
		concatGoals(game).map((goal) => ({
			...goal,
			extraData: { type: 'goal', team: goal.extraData.team }
		}))
	);
	let penalties = $derived.by(() =>
		concatPenalties(game).map((penalty) => ({
			...penalty,
			extraData: { type: 'penalty', team: penalty.extraData.team }
		}))
	);

	interface PeriodMarker {
		gameTime: number;
		gameTimePeriod: string;
		gameTimeFormatted: string;
		text: string;
	}

	let events = $derived.by(() => {
		let events: HDExtraData<HDGoal | HDPenalty | PeriodMarker>[] = [...goals, ...penalties].sort(
			(a, b) => b.gameTime - a.gameTime
		);
		const lastEvent = events[0];
		const start: HDExtraData<PeriodMarker> = {
			id: -1,
			gameTime: 0,
			gameTimePeriod: '1',
			gameTimeFormatted: '00:00',
			text: 'START',
			extraData: { type: 'period-end' }
		};
		const end1: HDExtraData<PeriodMarker> = {
			id: -1,
			gameTime: 20 * 60,
			gameTimePeriod: '1',
			gameTimeFormatted: '20:00',
			text: 'END-1',
			extraData: { type: 'period-end' }
		};
		const end2: HDExtraData<PeriodMarker> = {
			id: -1,
			gameTime: 40 * 60,
			gameTimePeriod: '2',
			gameTimeFormatted: '40:00',
			text: 'END-2',
			extraData: { type: 'period-end' }
		};
		const end3: HDExtraData<PeriodMarker> = {
			id: -1,
			gameTime: 60 * 60,
			gameTimePeriod: '3',
			gameTimeFormatted: '60:00',
			text: 'END-3',
			extraData: { type: 'period-end' }
		};
		const endOT: HDExtraData<PeriodMarker> = {
			id: -1,
			gameTime: 64.99 * 60, // just before 65, to keep PS goals above it
			gameTimePeriod: 'OT',
			gameTimeFormatted: '65:00',
			text: 'END-OT',
			extraData: { type: 'period-end' }
		};
		const endPS: HDExtraData<PeriodMarker> = {
			id: -1,
			gameTime: 70 * 60, // could be anything over 65 really
			gameTimePeriod: 'SO',
			gameTimeFormatted: '70:00',
			text: 'END-PS',
			extraData: { type: 'period-end' }
		};
		const endGame: HDExtraData<PeriodMarker> = {
			id: -1,
			gameTime: 70 * 60, // could be anything over 65 really
			gameTimePeriod: 'SO',
			gameTimeFormatted: '70:00',
			text: 'END-GAME',
			extraData: { type: 'period-end' }
		};
		events.push(start);

		let periodIndex =
			game.gameData.gameStatus >= GameStatus.ENDED
				? 4
				: ['1', '2', '3', 'OT', 'SO'].indexOf(game.gameData.liveTimePeriod);

		if (periodIndex > 0) {
			events.push(end1);
		}
		if (periodIndex > 1) {
			events.push(end2);
		}
		if (periodIndex > 2) {
			events.push(end3);
		}
		if (periodIndex > 3) {
			events.push(endOT);
		}
		if (game.gameData.gameStatus >= GameStatus.ENDED) {
			events.push(endGame);
			if (lastEvent.gameTimePeriod === 'SO') {
				events.push(endPS);
			}
		}

		return events.sort((a, b) => b.gameTime - a.gameTime);
	});

	// insert markers for end of periods after 20, 40 and 60 minutes
	// to be able to display the correct period in the timeline

	function isGoal(event: HDExtraData<any>): event is HDExtraData<HDGoal> {
		return event.extraData.type === 'goal';
	}

	function isPenalty(event: HDExtraData<any>): event is HDExtraData<HDPenalty> {
		return event.extraData.type === 'penalty';
	}

	function isPeriodmarker(event: HDExtraData<any>): event is HDExtraData<PeriodMarker> {
		return event.extraData.type === 'period-end';
	}
</script>

<CollapsibleCard
	title={`Live Timeline (${game.gameData.liveTimeFormatted})`}
	open={game.gameData.gameStatus == GameStatus.LIVE}
>
	<div class="flex w-full flex-row gap-2">
		{#each game.gameData.liveTimePeriodBar as period}
			<div class="bg-neutral flex h-8 flex-col" style={`width: ${period.maxLength}%`}>
				<div class="h-6 w-full text-center">{period.shortName}</div>
				<div class="bg-accent h-2" style={`width: ${period.progress}%`}></div>
			</div>
		{/each}
	</div>
	<ul class="timeline timeline-vertical max-md:timeline-compact">
		{#each events as event}
			<li class="timeline-item">
				<hr class="bg-accent timeline-middle" />
				{#if isGoal(event)}
					<div class="timeline-middle">🥅</div>
					<div
						class={[
							'timeline-box border-accent/50 text-lg',
							event.extraData.team === game.gameData.homeTeamShortname
								? 'timeline-start'
								: 'timeline-end'
						]}
					>
						<span>
							<span class="font-bold">{event.gameTimeFormatted}:</span>
							<span class="text-accent font-bold">Tor</span> für
							<span class="text-accent font-bold">{event.extraData.team}</span>
							von
							<span class="text-accent font-bold"
								>#{event.scoredBy.playerJerseyNr}
								{event.scoredBy.playerLastname}
								{event.scoredBy.playerFirstname.charAt(0)}.
							</span>
						</span>
						{#if event.assistBy}
							<span>
								nach Assist von
								<span class="text-accent font-bold">
									#{event.assistBy.playerJerseyNr}
									{event.assistBy.playerLastname}
									{event.assistBy.playerFirstname.charAt(0)}.
								</span>
							</span>
							{#if event.assist2By}
								<span>
									und
									<span class="text-accent font-bold">
										#{event.assist2By.playerJerseyNr}
										{event.assist2By.playerLastname}
										{event.assist2By.playerFirstname.charAt(0)}.
									</span>
								</span>
							{/if}
						{/if}
						<span
							>Neuer Spielstand: <span class="text-accent font-bold">{event.newScore}</span></span
						>
					</div>
				{:else if isPenalty(event)}
					<div class="timeline-middle">💥</div>
					<div
						class={[
							'timeline-box border-accent/50 text-lg',
							event.extraData.team === game.gameData.homeTeamShortname
								? 'timeline-start'
								: 'timeline-end'
						]}
					>
						<span class="font-bold">{event.gameTimeFormatted}</span>
						<span class="text-accent font-bold">
							{event.penaltyLength / 60} Strafminuten
						</span>
						für
						<span class="text-accent font-bold">
							#{event.offender.playerJerseyNr}
							{event.offender.playerLastname}
							{event.offender.playerFirstname.charAt(0)}.
						</span>
						(<span class="text-accent font-bold">{event.extraData.team}</span>) wegen
						<span class="text-accent font-bold">{event.offence}</span>
					</div>
				{:else if isPeriodmarker(event)}
					<div
						class="timeline-middle timeline-box border-accent my-3 hidden py-1 text-xl font-bold md:block"
					>
						{event.text}
					</div>
					<div class="timeline-middle block md:hidden">🔴</div>
					<div
						class="timeline-start timeline-box border-accent my-3 block py-1 text-xl font-bold md:hidden"
					>
						{event.text}
					</div>
				{/if}
				<hr class="bg-accent" />
			</li>
		{/each}
	</ul>
</CollapsibleCard>
